"use client";

import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Shield,
  ShieldCheck,
  Loader2,
  Copy,
  Download,
  AlertTriangle,
  Smartphone,
  KeyRound,
  Info,
} from "lucide-react";
import {
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@repo/ui";

import { setupStaff2fa, verifyStaff2faSetup } from "@/lib/api/auth";

type SetupStep = "intro" | "qr" | "codes";

interface TwoFactorSetupDialogProps {
  open: boolean;
  onComplete: () => void;
  onCancel?: () => void;
  /** Show a warning banner that 2FA is required by the pharmacy */
  enforced?: boolean;
  userInitials: string;
}

export function TwoFactorSetupDialog({
  open,
  onComplete,
  onCancel,
  enforced,
  userInitials,
}: TwoFactorSetupDialogProps) {
  const [step, setStep] = useState<SetupStep>("intro");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [qrUri, setQrUri] = useState("");
  const [secret, setSecret] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  // Reset all state when the dialog opens so each run starts clean
  useEffect(() => {
    if (open) {
      setStep("intro");
      setIsLoading(false);
      setError("");
      setQrUri("");
      setSecret("");
      setVerifyCode("");
      setRecoveryCodes([]);
      setCopied(false);
    }
  }, [open]);

  const handleProceedToQr = async () => {
    setIsLoading(true);
    setError("");
    try {
      const res = await setupStaff2fa();
      setQrUri(res.qrUri);
      setSecret(res.secret);
      setStep("qr");
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to start setup";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const res = await verifyStaff2faSetup({
        code: verifyCode,
        type: "totp",
      });
      setRecoveryCodes(res.recoveryCodes);
      setStep("codes");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Invalid code";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogOpenChange = (isOpen: boolean) => {
    // Block all dismissal during codes step
    if (!isOpen && step === "codes") return;
    if (!isOpen && onCancel) onCancel();
  };

  const copyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const downloadRecoveryCodes = () => {
    const content = `C2 Vault Recovery Codes for ${userInitials}\n${"=".repeat(40)}\n\n${recoveryCodes.join("\n")}\n\nKeep these codes in a safe place.\nEach code can only be used once.`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `c2vault-recovery-codes-${userInitials.toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => {
          if (step === "codes") e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (step === "codes") e.preventDefault();
        }}
      >
        {/* Step 1: Intro */}
        {step === "intro" && (
          <>
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <DialogTitle className="text-center">
                Set Up Two-Factor Authentication
              </DialogTitle>
              <DialogDescription className="text-center">
                Add an extra layer of security to your account
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {enforced && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-800">
                      Required by your pharmacy
                    </p>
                    <p className="text-xs text-amber-700">
                      Your pharmacy administrator requires all staff to enable
                      two-factor authentication before accessing the system.
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                  <Smartphone className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">
                      You'll need an authenticator app
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Google Authenticator, Authy, or Microsoft Authenticator.
                      Download one if you don't have it already.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                  <KeyRound className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">
                      You'll get recovery codes
                    </p>
                    <p className="text-xs text-muted-foreground">
                      10 one-time backup codes in case you lose access to your
                      authenticator app. Save them somewhere safe.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                  <ShieldCheck className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">What changes</p>
                    <p className="text-xs text-muted-foreground">
                      After setup, you'll enter a 6-digit code from your
                      authenticator app each time you log in with your initials
                      and password.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button
                onClick={handleProceedToQr}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Continue
              </Button>
              {onCancel && (
                <Button variant="ghost" onClick={onCancel} className="w-full">
                  {enforced ? "Set up later" : "Cancel"}
                </Button>
              )}
            </DialogFooter>
          </>
        )}

        {/* Step 2: QR Code */}
        {step === "qr" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-center">Scan QR Code</DialogTitle>
              <DialogDescription className="text-center">
                Open your authenticator app and scan this code to link your
                account
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 py-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-xl border shadow-sm">
                  <QRCodeSVG value={qrUri} size={180} />
                </div>
              </div>

              <details className="group">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Can't scan? Enter this code manually
                </summary>
                <code className="block mt-2 p-3 rounded-lg bg-muted text-xs font-mono break-all select-all">
                  {secret}
                </code>
              </details>

              <form onSubmit={handleVerify} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="setup-2fa-verify-code" className="text-sm">
                    Enter the 6-digit code from your app
                  </Label>
                  <Input
                    id="setup-2fa-verify-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) =>
                      setVerifyCode(e.target.value.replace(/\D/g, ""))
                    }
                    autoComplete="one-time-code"
                    autoFocus
                    className="text-center text-lg tracking-widest font-mono"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || verifyCode.length !== 6}
                  className="w-full"
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Verify & Activate
                </Button>
              </form>
            </div>
          </>
        )}

        {/* Step 3: Recovery Codes */}
        {step === "codes" && recoveryCodes.length > 0 && (
          <>
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <ShieldCheck className="h-6 w-6 text-green-600" />
              </div>
              <DialogTitle className="text-center">
                2FA is now active
              </DialogTitle>
              <DialogDescription className="text-center">
                Save your recovery codes before closing this window
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-800">
                    Save these codes now
                  </p>
                  <p className="text-xs text-amber-700">
                    Each code can only be used once. If you lose your
                    authenticator device, these are the only way to access your
                    account. You won't see them again.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 p-4 rounded-lg bg-muted">
                {recoveryCodes.map((code, i) => (
                  <code key={i} className="text-sm font-mono text-center py-1">
                    {code}
                  </code>
                ))}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyRecoveryCodes}
                  className="gap-2 flex-1"
                >
                  <Copy className="h-4 w-4" />
                  {copied ? "Copied!" : "Copy All"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadRecoveryCodes}
                  className="gap-2 flex-1"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={onComplete} className="w-full">
                I've saved my codes
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
